import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { ChevronLeft, ChevronRight, History, RotateCw } from 'lucide-react';
import api from '../../services/api';
import { sbg } from '../../theme/sbgTokens';
import {
  CountBadge,
  DefinitionList,
  EmptyState,
  ErrorState,
  FilterChip,
  KpiGrid,
  KpiTile,
  PageHeader,
  PersonCell,
  SearchField,
  SectionCard,
  SegmentedControl,
  Sheet,
  SpecGrid,
  StageRail,
  StatusPill,
  TableCard,
  TableEmptyRow,
  TableSkeletonRows,
  Toolbar,
  useDelayedFlag,
} from '../../components/ui';
import type { SpecItem, StageState, StatusTone } from '../../components/ui';
import {
  MISSING,
  formatCount,
  formatDate,
  formatDateRange,
  formatNumber,
  formatPercent,
  formatTime,
} from '../../utils/format';
import { EASTERN_TIME_ZONE, addDaysToYmd, getTodayEt } from '../../utils/easternTime';

interface SalesOfficeCheckinRow {
  employeeId: string;
  name: string;
  email: string;
  teamName: string | null;
  expectedInOffice: boolean;
  exclusionReason: string | null;
  status: string;
  statusLabel: string;
  statusReason: string | null;
  emailSentAt: string | null;
  emailDeliveryStatus: string;
  emailAttemptCount: number;
  emailLastError: string | null;
  firstClickedAt: string | null;
  latestClickedAt: string | null;
  onTimeClickedAt: string | null;
  lateClickedAt: string | null;
  requestIp: string | null;
  requestIpAllowed: boolean | null;
  ops906NotifiedAt: string | null;
  ops1031NotifiedAt: string | null;
}

interface SalesOfficeCheckinData {
  workDate: string;
  workDateLabel: string;
  onTimeDeadlineLabel: string;
  opsOffEmailLabel: string;
  lateRecheckLabel: string;
  lateOpsEmailLabel: string;
  cronEnabled: boolean;
  allowedIpCount: number;
  fetchedAtLabel: string;
  roster: SalesOfficeCheckinRow[];
  summary: {
    totalSalesReps: number;
    expectedInOffice: number;
    excludedCount: number;
    onTimeCount: number;
    lateCount: number;
    offsiteCount: number;
    noClickCount: number;
    pendingCount: number;
    emailUnavailableCount: number;
  };
}

interface SalesOfficeCheckinAnalyticsPerson {
  employeeId: string;
  name: string;
  email: string;
  teamName: string | null;
  expectedDays: number;
  onTimeCount: number;
  lateCount: number;
  noClickCount: number;
  offsiteCount: number;
  pendingCount: number;
  issueCount: number;
  checkinCount: number;
  onTimeRate: number;
  issueRate: number;
  lateRate: number;
  averageCheckinMinutes: number | null;
  averageCheckinLabel: string | null;
  earliestCheckinAt: string | null;
  earliestCheckinLabel: string | null;
  consistencyScore: number;
}

interface SalesOfficeCheckinAnalyticsTeam {
  teamName: string;
  expectedDays: number;
  onTimeCount: number;
  issueCount: number;
  lateCount: number;
  noClickCount: number;
  offsiteCount: number;
  onTimeRate: number;
}

interface SalesOfficeCheckinAnalyticsData {
  startDate: string;
  endDate: string;
  daysRequested: number;
  recordsAnalyzed: number;
  expectedRecords: number;
  overallOnTimeRate: number;
  issueRate: number;
  lateRate: number;
  earliestCheckins: SalesOfficeCheckinAnalyticsPerson[];
  mostConsistent: SalesOfficeCheckinAnalyticsPerson[];
  chronicLate: SalesOfficeCheckinAnalyticsPerson[];
  mostMissed: SalesOfficeCheckinAnalyticsPerson[];
  teamReliability: SalesOfficeCheckinAnalyticsTeam[];
  employeeSummaries: SalesOfficeCheckinAnalyticsPerson[];
}

interface SalesOfficeCheckinEmployeeHistoryRow {
  workDate: string;
  workDateLabel: string;
  expectedInOffice: boolean;
  status: string;
  statusLabel: string;
  statusReason: string | null;
  firstClickedAt: string | null;
  firstClickedLabel: string | null;
  onTimeClickedAt: string | null;
  lateClickedAt: string | null;
  requestIpAllowed: boolean | null;
}

interface SalesOfficeCheckinEmployeeHistoryData {
  employee: SalesOfficeCheckinAnalyticsPerson;
  peerRanks: {
    peerCount: number;
    onTimeRateRank: number | null;
    averageCheckinRank: number | null;
    consistencyRank: number | null;
    issueRateRank: number | null;
  };
  history: SalesOfficeCheckinEmployeeHistoryRow[];
}

/* ------------------------------------------------------------------------ */
/* Presentation helpers                                                      */
/* ------------------------------------------------------------------------ */

/** Sentence-case labels and tones for each check-in status (book §11.11, EMS mapping). */
const STATUS_META: Record<string, { label: string; tone: StatusTone }> = {
  on_time: { label: 'On time', tone: 'success' },
  late_on_site: { label: 'Late arrival', tone: 'warning' },
  no_click: { label: 'No click', tone: 'danger' },
  offsite_click: { label: 'Offsite click', tone: 'danger' },
  pending: { label: 'Awaiting check-in', tone: 'info' },
  email_unavailable: { label: 'Email unavailable', tone: 'warning' },
  not_expected: { label: 'Not expected', tone: 'neutral' },
  no_record: { label: 'No record', tone: 'neutral' },
};

/** Order of the roster's status filter chips. */
const FILTER_ORDER: string[] = [
  'on_time',
  'late_on_site',
  'no_click',
  'offsite_click',
  'pending',
  'email_unavailable',
  'not_expected',
];

/**
 * The statuses the 9:02 AM ET ops email turns off, in the email's order
 * (buildOpsOffEmailBody: late arrivals, offsite clicks, no click). Reps whose
 * check-in email wasn't delivered are listed there as "Do not penalize".
 */
const OPS_OFF_STATUSES: string[] = ['late_on_site', 'offsite_click', 'no_click'];

const ANALYTICS_WINDOWS = ['14', '30', '60', '90'] as const;
type AnalyticsWindow = (typeof ANALYTICS_WINDOWS)[number];

const ANALYTICS_PREVIEW_ROWS = 5;
const ROSTER_COLUMNS = 10;

/**
 * The morning email sweep runs every 6 minutes from 8:02 to 8:56 AM ET
 * (salesOfficeCheckinJob cron `2-56/6 8 * * 1-5`); it isn't in the report payload.
 */
const CHECKIN_EMAIL_START = '8:02 AM ET';
const CHECKIN_EMAIL_END = '8:56 AM ET';
const CHECKIN_EMAIL_WINDOW = '8:02–8:56 AM ET';
const FALLBACK_LABELS = {
  cutoff: '9:01 AM ET',
  opsOff: '9:02 AM ET',
  recheck: '10:30 AM ET',
  lateOps: '10:31 AM ET',
};

const sentenceCase = (value: string) => {
  const text = value.replace(/_/g, ' ').trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : MISSING;
};

const statusMeta = (status: string, label?: string | null) =>
  STATUS_META[status] ?? { label: sentenceCase(label || status), tone: 'info' as StatusTone };

const CheckinStatusPill: React.FC<{ status: string; label?: string | null; reason?: string | null }> = ({
  status,
  label,
  reason,
}) => {
  const meta = statusMeta(status, label);
  return (
    <StatusPill tone={meta.tone} title={reason || undefined}>
      {meta.label}
    </StatusPill>
  );
};

/**
 * `requestIp` / `requestIpAllowed` describe the rep's latest click, not where
 * they checked in, so an on-time rep can show "Other network" for a later
 * click (a phone, or a mail scanner opening the link).
 */
const networkLabel = (allowed: boolean | null) => (allowed === null ? MISSING : allowed ? 'Office network' : 'Other network');

const checkedInAt = (row: { onTimeClickedAt: string | null; lateClickedAt: string | null }) =>
  row.onTimeClickedAt || row.lateClickedAt;

const formatRank = (rank: number | null, peerCount: number) =>
  rank ? `Rank ${formatNumber(rank)} of ${formatNumber(peerCount)}` : 'Not ranked';

/** A real calendar date in a sane range (Chrome's date field emits 0002-…, 0020-… while the year is typed). */
const isWorkDate = (value: string) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || value < '2000-01-01' || value > '2099-12-31') return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
};

const clockLabelToMinutes = (label: string) => {
  const match = /^(\d{1,2}):(\d{2})\s*(AM|PM)/i.exec(label.trim());
  if (!match) return null;
  const hour = (Number(match[1]) % 12) + (match[3].toUpperCase() === 'PM' ? 12 : 0);
  return hour * 60 + Number(match[2]);
};

const ET_CLOCK = new Intl.DateTimeFormat('en-US', {
  timeZone: EASTERN_TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/** Minutes after midnight, Eastern Time. */
const etMinutesOf = (date: Date) => {
  const parts = ET_CLOCK.formatToParts(date);
  const read = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? 0);
  return (read('hour') % 24) * 60 + read('minute');
};

const earliestTimestamp = (values: (string | null)[]) =>
  values.reduce<string | null>((earliest, value) => {
    if (!value || Number.isNaN(Date.parse(value))) return earliest;
    return !earliest || Date.parse(value) < Date.parse(earliest) ? value : earliest;
  }, null);

type ScheduleKey = 'email' | 'cutoff' | 'ops' | 'recheck' | 'late';

interface ScheduleStep {
  key: ScheduleKey;
  label: string;
  detail: string;
  /** Shown to people. */
  time: string;
  /** When the step is over (the end of the email sweep, else the step time). */
  dueBy: string;
}

const buildSchedule = (data: SalesOfficeCheckinData | null): ScheduleStep[] => {
  const cutoff = data?.onTimeDeadlineLabel || FALLBACK_LABELS.cutoff;
  const opsOff = data?.opsOffEmailLabel || FALLBACK_LABELS.opsOff;
  const recheck = data?.lateRecheckLabel || FALLBACK_LABELS.recheck;
  const lateOps = data?.lateOpsEmailLabel || FALLBACK_LABELS.lateOps;
  return [
    { key: 'email', label: 'Emails', detail: 'Check-in emails go out', time: CHECKIN_EMAIL_WINDOW, dueBy: CHECKIN_EMAIL_END },
    { key: 'cutoff', label: 'Cutoff', detail: 'On-time cutoff', time: cutoff, dueBy: cutoff },
    { key: 'ops', label: 'Ops list', detail: 'Turn-off list to ops', time: opsOff, dueBy: opsOff },
    { key: 'recheck', label: 'Recheck', detail: 'Late arrivals rechecked', time: recheck, dueBy: recheck },
    { key: 'late', label: 'Late list', detail: 'Late list to ops', time: lateOps, dueBy: lateOps },
  ];
};

interface ScheduleStatus {
  states: StageState[];
  notes: (string | null)[];
  doneCount: number;
  summary: string;
}

/**
 * What actually ran for the selected date. Sends count as done only on
 * evidence in the roster (emailSentAt, ops906NotifiedAt, ops1031NotifiedAt),
 * or, for an ops list whose time has passed, when nobody belonged on it.
 * Deadlines (cutoff, recheck) count once their time has passed on a day the
 * check-in emails went out. Nothing is marked from the clock alone.
 */
const scheduleStatus = (steps: ScheduleStep[], data: SalesOfficeCheckinData | null, workDate: string): ScheduleStatus => {
  const blank = (summary: string): ScheduleStatus => ({
    states: steps.map(() => 'upcoming'),
    notes: steps.map(() => null),
    doneCount: 0,
    summary,
  });
  if (!data) return blank('');
  const weekday = new Date(`${workDate}T12:00:00Z`).getUTCDay();
  if (weekday === 0 || weekday === 6) return blank('The check-in schedule runs on weekdays only.');
  const today = getTodayEt();
  if (workDate > today) return blank(`Runs on ${formatDate(workDate, { weekday: true })}.`);

  const isToday = workDate === today;
  const now = isToday ? etMinutesOf(new Date()) : Number.POSITIVE_INFINITY;
  const passed = (label: string) => {
    const minutes = clockLabelToMinutes(label);
    return minutes !== null && minutes <= now;
  };
  const step = (key: ScheduleKey) => steps.find((item) => item.key === key) as ScheduleStep;

  const roster = data.roster;
  const emailsSent = roster.filter((row) => row.emailSentAt).length;
  const started = emailsSent > 0;
  const opsSentAt = earliestTimestamp(roster.map((row) => row.ops906NotifiedAt));
  const lateSentAt = earliestTimestamp(roster.map((row) => row.ops1031NotifiedAt));
  const opsOffCount = roster.filter((row) => row.expectedInOffice && OPS_OFF_STATUSES.includes(row.status)).length;
  const recheckMinutes = clockLabelToMinutes(step('recheck').time) ?? 10 * 60 + 30;
  const turnBackOnCount = roster.filter(
    (row) =>
      row.expectedInOffice &&
      row.status === 'late_on_site' &&
      row.lateClickedAt &&
      etMinutesOf(new Date(row.lateClickedAt)) <= recheckMinutes
  ).length;

  const opsDue = started && passed(step('ops').dueBy);
  const lateDue = started && passed(step('late').dueBy);
  const done: Record<ScheduleKey, boolean> = {
    email: started,
    cutoff: started && passed(step('cutoff').dueBy),
    ops: Boolean(opsSentAt) || (opsDue && opsOffCount === 0),
    recheck: started && passed(step('recheck').dueBy),
    late: Boolean(lateSentAt) || (lateDue && turnBackOnCount === 0),
  };
  const notes: Record<ScheduleKey, string | null> = {
    email: started
      ? `Sent to ${formatCount(emailsSent, 'rep')}`
      : passed(step('email').dueBy)
        ? 'None sent for this date'
        : null,
    cutoff: null,
    ops: opsSentAt
      ? `Sent ${formatTime(opsSentAt)}`
      : done.ops
        ? 'No reps to turn off'
        : opsDue
          ? 'Not on record'
          : null,
    recheck: null,
    late: lateSentAt
      ? `Sent ${formatTime(lateSentAt)}`
      : done.late
        ? 'No late arrivals to turn back on'
        : lateDue
          ? 'Not on record'
          : null,
  };

  const flags = steps.map((item) => done[item.key]);
  const doneCount = flags.filter(Boolean).length;
  const live = isToday && data.cronEnabled;
  let summary: string;
  if (!started) {
    summary =
      isToday && !passed(step('email').dueBy)
        ? live
          ? `Check-in emails start at ${CHECKIN_EMAIL_START}.`
          : 'Nothing has run for this date yet.'
        : 'No check-in emails went out for this date.';
  } else {
    const next = isToday ? steps.find((item, index) => !flags[index] && !passed(item.dueBy)) : undefined;
    const missing = steps.find((_, index) => !flags[index]);
    if (next) {
      summary = live
        ? `Next: ${next.detail.toLowerCase()} at ${next.time}.`
        : `${formatNumber(doneCount)} of ${formatCount(steps.length, 'step')} on record so far.`;
    } else if (missing) {
      summary = `${missing.detail} isn’t on record for this date.`;
    } else {
      summary = isToday ? 'Today’s schedule is complete.' : 'This date’s schedule is complete.';
    }
  }

  return {
    states: flags.map((flag) => (flag ? 'done' : 'upcoming')),
    notes: steps.map((item) => notes[item.key]),
    doneCount,
    summary,
  };
};

const insetFocus = { outline: `2px solid ${sbg.focus.ring}`, outlineOffset: -2 } as const;

/** A clickable list row: hover wash, inset focus ring, full-width hit target. */
const listRowSx = (columns: string): SxProps<Theme> => ({
  width: '100%',
  textAlign: 'left',
  display: 'grid',
  gridTemplateColumns: columns,
  alignItems: 'center',
  gap: 1.5,
  px: 2.5,
  py: 1.25,
  borderBottom: `1px solid ${sbg.border.subtle}`,
  transition: `background-color ${sbg.duration.fast} ${sbg.ease.standard}`,
  '&:hover': { bgcolor: sbg.bg.hover },
  '&.Mui-focusVisible': insetFocus,
});

/* ------------------------------------------------------------------------ */
/* Section heading                                                           */
/* ------------------------------------------------------------------------ */

const SectionHeading: React.FC<{
  id: string;
  title: string;
  description?: React.ReactNode;
  actions?: React.ReactNode;
}> = ({ id, title, description, actions }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: { xs: 'column', md: 'row' },
      alignItems: { xs: 'stretch', md: 'flex-end' },
      justifyContent: 'space-between',
      gap: 2,
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      <Typography id={id} variant="h3" component="h2" sx={{ color: sbg.text.primary }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body1" component="p" sx={{ color: sbg.text.secondary, mt: 0.5 }}>
          {description}
        </Typography>
      )}
    </Box>
    {actions && (
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, flexWrap: 'wrap' }}>{actions}</Box>
    )}
  </Box>
);

/* ------------------------------------------------------------------------ */
/* Turn-off list (the 9:02 AM ET ops list)                                   */
/* ------------------------------------------------------------------------ */

const PersonRowSkeleton: React.FC = () => (
  <Box aria-hidden="true" sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2.5, py: 1.25 }}>
    <Skeleton variant="circular" width={32} height={32} />
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Skeleton variant="rounded" width="40%" height={14} />
      <Skeleton variant="rounded" width="64%" height={12} sx={{ mt: 0.75 }} />
    </Box>
    <Skeleton variant="rounded" width={88} height={22} sx={{ borderRadius: sbg.radius.pill }} />
  </Box>
);

const TurnOffList: React.FC<{
  rows: SalesOfficeCheckinRow[];
  busy: boolean;
  showSkeleton: boolean;
  expected: number;
  excused: number;
  pending: number;
  opsOffLabel: string;
  cutoffLabel: string;
  onSelect: (employeeId: string) => void;
}> = ({ rows, busy, showSkeleton, expected, excused, pending, opsOffLabel, cutoffLabel, onSelect }) => {
  const footnote = [
    excused > 0 ? `${formatCount(excused, 'rep')} excused (email not delivered)` : null,
    pending > 0 ? `${formatCount(pending, 'rep')} awaiting check-in` : null,
  ].filter(Boolean);

  return (
    <SectionCard
      title={`Turn off at ${opsOffLabel}`}
      description={`Expected reps who were late, clicked offsite, or didn’t click by ${cutoffLabel}. This is the list ops gets at ${opsOffLabel}.`}
      action={
        !busy && rows.length > 0 ? <CountBadge count={rows.length} label={formatCount(rows.length, 'rep to turn off', 'reps to turn off')} /> : undefined
      }
      footer={
        !busy && footnote.length > 0 ? (
          <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}>
            {footnote.join(' · ')}
          </Typography>
        ) : undefined
      }
      flush
    >
      {busy ? (
        <Box role="status" aria-label="Loading turn-off list" sx={{ visibility: showSkeleton ? 'visible' : 'hidden' }}>
          {[0, 1, 2, 3, 4].map((n) => (
            <PersonRowSkeleton key={n} />
          ))}
        </Box>
      ) : rows.length === 0 ? (
        <EmptyState
          compact
          title={expected === 0 ? 'No one is expected in the office' : pending > 0 ? 'No one to turn off yet' : 'No one to turn off'}
          body={
            expected === 0
              ? 'No sales reps are due in the office on this date.'
              : pending > 0
                ? `Reps without an office check-in by ${cutoffLabel} are listed here.`
                : `Every eligible rep checked in from the office by ${cutoffLabel}.`
          }
        />
      ) : (
        <Box
          component="ul"
          aria-label={`Turn off at ${opsOffLabel}`}
          tabIndex={0}
          sx={{ listStyle: 'none', m: 0, p: 0, maxHeight: 392, overflowY: 'auto', '&:focus-visible': insetFocus }}
        >
          {rows.map((row) => {
            const status = statusMeta(row.status, row.statusLabel).label;
            const detail = row.lateClickedAt
              ? `Checked in ${formatTime(row.lateClickedAt)}`
              : row.status === 'offsite_click' && row.requestIp
                ? `Clicked from ${row.requestIp}`
                : row.statusReason || status;
            const sentToOps = row.ops906NotifiedAt ? `Sent to ops ${formatTime(row.ops906NotifiedAt)}` : null;
            return (
              <Box component="li" key={row.employeeId} sx={{ '&:last-of-type > *': { borderBottom: 0 } }}>
                <ButtonBase
                  onClick={() => onSelect(row.employeeId)}
                  aria-label={`${row.name}, ${status}${sentToOps ? `. ${sentToOps}` : ''}. View check-in history`}
                  sx={listRowSx('minmax(0, 1fr) auto')}
                >
                  <PersonCell name={row.name} secondary={`${row.teamName || 'Sales'} · ${detail}`} />
                  <Box sx={{ display: 'grid', justifyItems: 'end', gap: 0.5 }}>
                    <CheckinStatusPill status={row.status} label={row.statusLabel} reason={row.statusReason} />
                    {sentToOps && (
                      <Typography
                        variant="caption"
                        component="span"
                        sx={{ color: sbg.text.secondary, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
                      >
                        {sentToOps}
                      </Typography>
                    )}
                  </Box>
                </ButtonBase>
              </Box>
            );
          })}
        </Box>
      )}
    </SectionCard>
  );
};

/* ------------------------------------------------------------------------ */
/* Schedule & policy                                                         */
/* ------------------------------------------------------------------------ */

const ScheduleCard: React.FC<{
  data: SalesOfficeCheckinData | null;
  busy: boolean;
  showSkeleton: boolean;
  workDate: string;
}> = ({ data, busy, showSkeleton, workDate }) => {
  const steps = buildSchedule(data);
  const status = scheduleStatus(steps, busy ? null : data, workDate);
  return (
    <SectionCard title="Schedule" description="Weekday automation for the sales floor. All times ET.">
      <Box sx={{ display: 'grid', gap: 2.5 }}>
        <Box sx={{ display: 'grid', gap: 1 }}>
          <StageRail
            aria-label={
              busy
                ? 'Check-in schedule'
                : `Check-in schedule: ${formatNumber(status.doneCount)} of ${formatCount(steps.length, 'step')} done`
            }
            stages={steps.map((step, index) => ({ label: step.label, state: status.states[index] }))}
          />
          <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, minHeight: 18 }}>
            {busy ? '\u00a0' : status.summary}
          </Typography>
        </Box>
        {busy ? (
          <Box aria-hidden="true" sx={{ display: 'grid', gap: 1.5, visibility: showSkeleton ? 'visible' : 'hidden' }}>
            {[0, 1, 2, 3, 4, 5].map((n) => (
              <Skeleton key={n} variant="rounded" height={16} width={`${70 - n * 5}%`} />
            ))}
          </Box>
        ) : (
          <DefinitionList
            items={[
              ...steps.map((step, index) => ({
                label: step.detail,
                value: (
                  <Box component="span" sx={{ display: 'grid', gap: 0.25 }}>
                    <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {step.time}
                    </Box>
                    {status.notes[index] && (
                      <Typography variant="body2" component="span" sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}>
                        {status.notes[index]}
                      </Typography>
                    )}
                  </Box>
                ),
              })),
              {
                label: 'Office networks',
                value: data ? formatCount(data.allowedIpCount, 'allowed IP address', 'allowed IP addresses') : MISSING,
              },
              {
                label: 'Automation',
                value: data ? (
                  <StatusPill tone={data.cronEnabled ? 'success' : 'warning'}>{data.cronEnabled ? 'Running' : 'Paused'}</StatusPill>
                ) : (
                  MISSING
                ),
              },
            ]}
          />
        )}
      </Box>
    </SectionCard>
  );
};

/* ------------------------------------------------------------------------ */
/* Roster                                                                    */
/* ------------------------------------------------------------------------ */

const stickyCellSx: SxProps<Theme> = {
  position: 'sticky',
  left: 0,
  zIndex: 1,
  bgcolor: sbg.bg.surface,
  '.MuiTableRow-root:hover > &': { bgcolor: sbg.bg.hover },
};

const stickyHeadSx: SxProps<Theme> = {
  position: 'sticky',
  left: 0,
  zIndex: 3,
  bgcolor: sbg.bg.surfaceSubtle,
};

const timeCellSx: SxProps<Theme> = { whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };

const NotesText: React.FC<{ text: string | null }> = ({ text }) => {
  if (!text) {
    return (
      <Typography variant="body2" component="span" sx={{ color: sbg.text.tertiary }}>
        {MISSING}
      </Typography>
    );
  }
  const body = (
    <Typography variant="body2" component="span" noWrap sx={{ display: 'block', maxWidth: 280, color: sbg.text.secondary }}>
      {text}
    </Typography>
  );
  return text.length > 44 ? <Tooltip title={text}>{body}</Tooltip> : body;
};

const RosterTable: React.FC<{
  roster: SalesOfficeCheckinRow[];
  busy: boolean;
  dateLabel: string;
  onSelect: (employeeId: string) => void;
}> = ({ roster, busy, dateLabel, onSelect }) => {
  const [search, setSearch] = useState('');
  const [statuses, setStatuses] = useState<string[]>([]);

  const counts = useMemo(
    () =>
      roster.reduce<Record<string, number>>((acc, row) => {
        acc[row.status] = (acc[row.status] || 0) + 1;
        return acc;
      }, {}),
    [roster]
  );

  const chipStatuses = useMemo(() => {
    const known = FILTER_ORDER.filter((status) => counts[status] || statuses.includes(status));
    const extra = Object.keys(counts).filter((status) => !FILTER_ORDER.includes(status));
    return known.concat(extra);
  }, [counts, statuses]);

  const query = search.trim().toLowerCase();
  const visible = useMemo(
    () =>
      roster.filter(
        (row) =>
          (statuses.length === 0 || statuses.includes(row.status)) &&
          (!query || [row.name, row.email, row.teamName || 'Sales'].some((value) => value.toLowerCase().includes(query)))
      ),
    [roster, statuses, query]
  );

  const filtersActive = query !== '' || statuses.length > 0;
  const clearFilters = () => {
    setSearch('');
    setStatuses([]);
  };
  const toggleStatus = (status: string) =>
    setStatuses((current) => (current.includes(status) ? current.filter((s) => s !== status) : current.concat(status)));

  return (
    <TableCard
      aria-label="Check-in roster table"
      maxHeight={640}
      toolbar={
        <Toolbar
          end={
            !busy && roster.length > 0 ? (
              <Typography variant="body2" component="p" aria-live="polite" sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}>
                {filtersActive
                  ? `${formatNumber(visible.length)} of ${formatCount(roster.length, 'rep')}`
                  : formatCount(roster.length, 'rep')}
              </Typography>
            ) : undefined
          }
        >
          <SearchField value={search} onChange={setSearch} label="Search reps" placeholder="Search name, email or team" width={260} />
          {chipStatuses.length > 0 && (
            <Box role="group" aria-label="Filter by status" sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {chipStatuses.map((status) => (
                <FilterChip
                  key={status}
                  selected={statuses.includes(status)}
                  onClick={() => toggleStatus(status)}
                  count={counts[status] || 0}
                >
                  {statusMeta(status).label}
                </FilterChip>
              ))}
            </Box>
          )}
        </Toolbar>
      }
    >
      {/* Empty states sit outside the wide table so they stay centered in the
          visible viewport instead of the middle of a 1120px scroll width. */}
      {!busy && roster.length === 0 ? (
        <EmptyState
          compact
          title="No sales reps on this roster"
          body={`Reps appear here when they’re on the sales roster for ${dateLabel}.`}
        />
      ) : !busy && visible.length === 0 ? (
        <EmptyState
          compact
          title="No reps match these filters"
          body="Try another name, or clear the search and status filters."
          action={
            <Button variant="outlined" size="small" onClick={clearFilters}>
              Clear filters
            </Button>
          }
        />
      ) : (
        <Table stickyHeader size="small" sx={{ minWidth: 1120 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={stickyHeadSx}>Employee</TableCell>
              <TableCell>Team</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Email sent</TableCell>
              <TableCell align="right">First click</TableCell>
              <TableCell align="right">Checked in</TableCell>
              <TableCell>Last click from</TableCell>
              <TableCell>IP address</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell align="right">
                <span className="sbg-sr-only">Actions</span>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {busy ? (
              <TableSkeletonRows columns={ROSTER_COLUMNS} rows={8} />
            ) : (
              visible.map((row) => (
                <TableRow key={row.employeeId}>
                  <TableCell sx={stickyCellSx}>
                    <PersonCell
                      name={row.name}
                      secondary={
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                          {row.email}
                        </Box>
                      }
                      sx={{ maxWidth: { xs: 148, sm: 260 } }}
                    />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.teamName || 'Sales'}</TableCell>
                  <TableCell>
                    <CheckinStatusPill status={row.status} label={row.statusLabel} reason={row.statusReason} />
                  </TableCell>
                  <TableCell align="right" sx={timeCellSx}>{formatTime(row.emailSentAt)}</TableCell>
                  <TableCell align="right" sx={timeCellSx}>{formatTime(row.firstClickedAt)}</TableCell>
                  <TableCell align="right" sx={timeCellSx}>{formatTime(checkedInAt(row))}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{networkLabel(row.requestIpAllowed)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="mono" component="span" sx={{ color: row.requestIp ? sbg.text.secondary : sbg.text.tertiary }}>
                      {row.requestIp || MISSING}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <NotesText text={row.exclusionReason || row.statusReason} />
                  </TableCell>
                  <TableCell align="right" sx={{ width: 56 }}>
                    <Tooltip title="View check-in history">
                      <IconButton size="small" aria-label={`View check-in history for ${row.name}`} onClick={() => onSelect(row.employeeId)}>
                        <History />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}
    </TableCard>
  );
};

/* ------------------------------------------------------------------------ */
/* Trends                                                                    */
/* ------------------------------------------------------------------------ */

const Leaderboard: React.FC<{
  title: string;
  subtitle: string;
  rows: SalesOfficeCheckinAnalyticsPerson[];
  emptyText: string;
  metricLabel: string;
  metricFor: (row: SalesOfficeCheckinAnalyticsPerson) => string;
  detailFor: (row: SalesOfficeCheckinAnalyticsPerson) => string;
  onSelect: (employeeId: string) => void;
}> = ({ title, subtitle, rows, emptyText, metricLabel, metricFor, detailFor, onSelect }) => {
  const [expanded, setExpanded] = useState(false);
  const visibleRows = expanded ? rows : rows.slice(0, ANALYTICS_PREVIEW_ROWS);
  const canExpand = rows.length > ANALYTICS_PREVIEW_ROWS;

  return (
    <SectionCard
      title={title}
      description={subtitle}
      action={rows.length > 0 ? <CountBadge count={rows.length} label={formatCount(rows.length, 'rep')} /> : undefined}
      flush
      sx={{ height: '100%' }}
      footer={
        canExpand ? (
          <Button size="small" variant="text" onClick={() => setExpanded((current) => !current)} aria-expanded={expanded}>
            {expanded ? `Show top ${ANALYTICS_PREVIEW_ROWS}` : `Show all ${formatNumber(rows.length)}`}
          </Button>
        ) : undefined
      }
    >
      {rows.length === 0 ? (
        <Typography variant="body1" component="p" sx={{ color: sbg.text.secondary, px: 2.5, py: 2 }}>
          {emptyText}
        </Typography>
      ) : (
        <Box
          component="ol"
          aria-label={title}
          sx={{ listStyle: 'none', m: 0, p: 0, ...(expanded ? { maxHeight: 420, overflowY: 'auto' } : null) }}
        >
          {visibleRows.map((row, index) => (
            <Box component="li" key={row.employeeId} sx={{ '&:last-of-type > *': { borderBottom: 0 } }}>
              <ButtonBase
                onClick={() => onSelect(row.employeeId)}
                sx={listRowSx('20px minmax(0, 1fr) auto')}
              >
                <Typography variant="labelSm" component="span" sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}>
                  {index + 1}
                </Typography>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="labelMd" component="span" noWrap sx={{ display: 'block', color: sbg.text.primary }}>
                    {row.name}
                  </Typography>
                  <Typography variant="body2" component="span" noWrap sx={{ display: 'block', color: sbg.text.secondary }}>
                    {row.teamName || 'Sales'} · {detailFor(row)}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <Typography variant="labelMd" component="span" sx={{ display: 'block', color: sbg.text.primary, fontVariantNumeric: 'tabular-nums' }}>
                    {metricFor(row)}
                  </Typography>
                  <Typography variant="caption" component="span" sx={{ display: 'block', color: sbg.text.secondary }}>
                    {metricLabel}
                  </Typography>
                </Box>
              </ButtonBase>
            </Box>
          ))}
        </Box>
      )}
    </SectionCard>
  );
};

const TeamReliability: React.FC<{ teams: SalesOfficeCheckinAnalyticsTeam[] }> = ({ teams }) => (
  <SectionCard title="Team reliability" description="On-time rate by team across this window." flush>
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 760 }}>
        <TableHead>
          <TableRow>
            <TableCell>Team</TableCell>
            <TableCell align="right">Expected rep-days</TableCell>
            <TableCell align="right">On time</TableCell>
            <TableCell align="right">Late</TableCell>
            <TableCell align="right">No click</TableCell>
            <TableCell align="right">Offsite</TableCell>
            <TableCell align="right">Issues</TableCell>
            <TableCell align="right">On-time rate</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {teams.length === 0 ? (
            <TableEmptyRow colSpan={8} title="No team data in this window" body="Teams appear once their reps have expected office days." />
          ) : (
            teams.map((team) => (
              <TableRow key={team.teamName}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography variant="labelMd" component="span" sx={{ color: sbg.text.primary }}>
                    {team.teamName}
                  </Typography>
                </TableCell>
                <TableCell align="right">{formatNumber(team.expectedDays)}</TableCell>
                <TableCell align="right">{formatNumber(team.onTimeCount)}</TableCell>
                <TableCell align="right">{formatNumber(team.lateCount)}</TableCell>
                <TableCell align="right">{formatNumber(team.noClickCount)}</TableCell>
                <TableCell align="right">{formatNumber(team.offsiteCount)}</TableCell>
                <TableCell align="right">{formatNumber(team.issueCount)}</TableCell>
                <TableCell align="right">{formatPercent(team.onTimeRate)}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </Box>
  </SectionCard>
);

const TrendsSkeleton: React.FC = () => {
  const visible = useDelayedFlag(true, 300);
  return (
    <Box role="status" aria-label="Loading check-in trends" sx={{ display: 'grid', gap: 3, visibility: visible ? 'visible' : 'hidden' }}>
      <Box aria-hidden="true" sx={{ display: 'grid', gap: 4, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' } }}>
        {[0, 1, 2, 3].map((n) => (
          <Box key={n} sx={{ borderTop: `1px solid ${sbg.border.default}`, pt: 2, display: 'grid', gap: 1 }}>
            <Skeleton variant="rounded" width={96} height={12} />
            <Skeleton variant="rounded" width="50%" height={28} />
            <Skeleton variant="rounded" width="70%" height={14} />
          </Box>
        ))}
      </Box>
      <Box aria-hidden="true" sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' } }}>
        {[0, 1, 2, 3].map((n) => (
          <Skeleton key={n} variant="rounded" height={320} sx={{ borderRadius: sbg.radius.card }} />
        ))}
      </Box>
    </Box>
  );
};

/* ------------------------------------------------------------------------ */
/* Employee history sheet                                                    */
/* ------------------------------------------------------------------------ */

const EmployeeHistoryBody: React.FC<{
  history: SalesOfficeCheckinEmployeeHistoryData | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}> = ({ history, loading, error, onRetry }) => {
  const showSkeleton = useDelayedFlag(loading, 300);

  if (error) {
    return (
      <ErrorState
        compact
        title="Couldn’t load this rep’s history"
        body="The server didn’t return their check-in history. Try again in a minute."
        details={error}
        onRetry={onRetry}
      />
    );
  }

  if (loading || !history) {
    return (
      <Box role="status" aria-label="Loading check-in history" sx={{ display: 'grid', gap: 3, visibility: showSkeleton ? 'visible' : 'hidden' }}>
        <Box aria-hidden="true" sx={{ display: 'grid', gap: 3, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
          {[0, 1, 2, 3].map((n) => (
            <Box key={n} sx={{ borderTop: `1px solid ${sbg.border.default}`, pt: 2, display: 'grid', gap: 1 }}>
              <Skeleton variant="rounded" width={88} height={12} />
              <Skeleton variant="rounded" width="45%" height={24} />
              <Skeleton variant="rounded" width="65%" height={14} />
            </Box>
          ))}
        </Box>
        <Skeleton variant="rounded" height={280} sx={{ borderRadius: sbg.radius.lg }} />
      </Box>
    );
  }

  const { employee, peerRanks } = history;
  const specs: SpecItem[] = [
    {
      label: 'On-time rate',
      value: formatPercent(employee.onTimeRate),
      caption: `${formatRank(peerRanks.onTimeRateRank, peerRanks.peerCount)} · ${formatCount(employee.expectedDays, 'expected day')}`,
    },
    {
      label: 'Avg check-in',
      value: employee.averageCheckinLabel || MISSING,
      caption: formatRank(peerRanks.averageCheckinRank, peerRanks.peerCount),
    },
    {
      label: 'Consistency',
      value: formatNumber(employee.consistencyScore),
      caption: `Out of 100 · ${formatRank(peerRanks.consistencyRank, peerRanks.peerCount)}`,
    },
    {
      label: 'Issue rate',
      value: formatPercent(employee.issueRate),
      caption: formatRank(peerRanks.issueRateRank, peerRanks.peerCount),
    },
  ];
  const years = history.history.map((row) => row.workDate.slice(0, 4));
  const showYear = years.some((year) => year !== years[0]);

  return (
    <Box sx={{ display: 'grid', gap: 3 }}>
      <SpecGrid items={specs} minColumnWidth={200} />
      <Box sx={{ display: 'grid', gap: 1.5 }}>
        <Typography variant="h4" component="h3" sx={{ color: sbg.text.primary }}>
          Day by day
        </Typography>
        <TableCard aria-label="Check-in history by day" maxHeight={460}>
          <Table stickyHeader size="small" sx={{ minWidth: 620 }}>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">First click</TableCell>
                <TableCell align="right">Checked in</TableCell>
                <TableCell>Last click from</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.history.length === 0 ? (
                <TableEmptyRow colSpan={6} title="No check-in days in this window" body="Days appear here once this rep is on the sales roster." />
              ) : (
                history.history.map((row) => (
                  <TableRow key={row.workDate}>
                    <TableCell sx={timeCellSx}>{formatDate(row.workDate, { short: !showYear, weekday: true })}</TableCell>
                    <TableCell>
                      <CheckinStatusPill status={row.status} label={row.statusLabel} reason={row.statusReason} />
                    </TableCell>
                    <TableCell align="right" sx={timeCellSx}>
                      {row.firstClickedAt ? formatTime(row.firstClickedAt) : row.firstClickedLabel || MISSING}
                    </TableCell>
                    <TableCell align="right" sx={timeCellSx}>{formatTime(checkedInAt(row))}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{networkLabel(row.requestIpAllowed)}</TableCell>
                    <TableCell>
                      <NotesText text={row.statusReason || (row.expectedInOffice ? null : 'Not expected')} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableCard>
      </Box>
    </Box>
  );
};

/* ------------------------------------------------------------------------ */
/* Page                                                                      */
/* ------------------------------------------------------------------------ */

const SalesOfficeCheckinManager: React.FC = () => {
  const [data, setData] = useState<SalesOfficeCheckinData | null>(null);
  const [analytics, setAnalytics] = useState<SalesOfficeCheckinAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);
  // `selectedWorkDate` is always a valid date and drives every request;
  // `dateInput` mirrors the date field, which can be blank or mid-edit.
  const [selectedWorkDate, setSelectedWorkDate] = useState(getTodayEt());
  const [dateInput, setDateInput] = useState(selectedWorkDate);
  const [dateFocused, setDateFocused] = useState(false);
  const [analyticsDays, setAnalyticsDays] = useState(30);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [employeeHistory, setEmployeeHistory] = useState<SalesOfficeCheckinEmployeeHistoryData | null>(null);
  const [employeeHistoryLoading, setEmployeeHistoryLoading] = useState(false);
  const [employeeHistoryError, setEmployeeHistoryError] = useState<string | null>(null);
  const [reportUpdatedAt, setReportUpdatedAt] = useState<Date | null>(null);
  // Only the latest request of each kind may write state, so a slow response
  // for an earlier date or window never lands over a newer one.
  const reportRequestId = useRef(0);
  const analyticsRequestId = useRef(0);
  const employeeHistoryRequestId = useRef(0);

  const fetchReport = useCallback(async (workDate: string) => {
    const requestId = reportRequestId.current + 1;
    reportRequestId.current = requestId;
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/sales-office-checkin/admin/daily', {
        params: { workDate },
      });
      if (reportRequestId.current === requestId) {
        setData(response.data?.data || null);
        setReportUpdatedAt(new Date());
      }
    } catch (err: any) {
      if (reportRequestId.current === requestId) {
        setError(err?.response?.data?.error || 'Failed to load sales office check-in report');
        setReportUpdatedAt(null);
      }
    } finally {
      if (reportRequestId.current === requestId) {
        setLoading(false);
      }
    }
  }, []);

  const fetchAnalytics = useCallback(async (endDate: string, days: number) => {
    const requestId = analyticsRequestId.current + 1;
    analyticsRequestId.current = requestId;
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    try {
      const response = await api.get('/sales-office-checkin/admin/analytics', {
        params: { endDate, days },
      });
      if (analyticsRequestId.current === requestId) {
        setAnalytics(response.data?.data || null);
      }
    } catch (err: any) {
      if (analyticsRequestId.current === requestId) {
        setAnalyticsError(err?.response?.data?.error || 'Failed to load sales office check-in analytics');
      }
    } finally {
      if (analyticsRequestId.current === requestId) {
        setAnalyticsLoading(false);
      }
    }
  }, []);

  const fetchEmployeeHistory = useCallback(async (employeeId: string, endDate: string, days: number) => {
    const requestId = employeeHistoryRequestId.current + 1;
    employeeHistoryRequestId.current = requestId;

    if (!employeeId) {
      setEmployeeHistory(null);
      setEmployeeHistoryError(null);
      setEmployeeHistoryLoading(false);
      return;
    }

    setEmployeeHistoryLoading(true);
    setEmployeeHistoryError(null);
    setEmployeeHistory(null);
    try {
      const response = await api.get(`/sales-office-checkin/admin/employees/${employeeId}/history`, {
        params: { endDate, days },
      });
      if (employeeHistoryRequestId.current === requestId) {
        setEmployeeHistory(response.data?.data || null);
      }
    } catch (err: any) {
      if (employeeHistoryRequestId.current === requestId) {
        setEmployeeHistory(null);
        setEmployeeHistoryError(err?.response?.data?.error || 'Failed to load employee check-in history');
      }
    } finally {
      if (employeeHistoryRequestId.current === requestId) {
        setEmployeeHistoryLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchReport(selectedWorkDate);
  }, [fetchReport, selectedWorkDate]);

  useEffect(() => {
    fetchAnalytics(selectedWorkDate, analyticsDays);
  }, [analyticsDays, fetchAnalytics, selectedWorkDate]);

  useEffect(() => {
    fetchEmployeeHistory(selectedEmployeeId, selectedWorkDate, analyticsDays);
  }, [analyticsDays, fetchEmployeeHistory, selectedEmployeeId, selectedWorkDate]);

  // Stale-while-revalidate: a reload of the same date keeps its report on
  // screen; skeletons only replace data that belongs to another date/window.
  const reportCurrent = Boolean(data && data.workDate === selectedWorkDate);
  const report = reportCurrent ? data : null;
  const reportBusy = loading && !reportCurrent;
  const showReportSkeleton = useDelayedFlag(reportBusy, 300);
  const analyticsCurrent = Boolean(
    analytics && analytics.endDate === selectedWorkDate && analytics.daysRequested === analyticsDays
  );
  const analyticsBusy = analyticsLoading && !analyticsCurrent;
  const analyticsRefreshing = analyticsLoading && analyticsCurrent;

  const opsOffRows = useMemo(
    () =>
      (report?.roster || [])
        .filter((row) => row.expectedInOffice && OPS_OFF_STATUSES.includes(row.status))
        .sort((left, right) => OPS_OFF_STATUSES.indexOf(left.status) - OPS_OFF_STATUSES.indexOf(right.status)),
    [report]
  );

  const employeeOptions = useMemo(
    () => [...(analytics?.employeeSummaries || [])].sort((left, right) => left.name.localeCompare(right.name)),
    [analytics]
  );

  const selectedPerson = useMemo(() => {
    if (!selectedEmployeeId) return null;
    const match =
      (employeeHistory?.employee.employeeId === selectedEmployeeId ? employeeHistory.employee : null) ||
      employeeOptions.find((person) => person.employeeId === selectedEmployeeId) ||
      data?.roster.find((row) => row.employeeId === selectedEmployeeId);
    return match ? { name: match.name, teamName: match.teamName } : null;
  }, [data, employeeHistory, employeeOptions, selectedEmployeeId]);

  const isToday = selectedWorkDate === getTodayEt();
  const dateLabel = formatDate(selectedWorkDate, { weekday: true });
  const shortDateLabel = formatDate(selectedWorkDate, { short: true });
  const dateInputInvalid = dateInput !== selectedWorkDate && !isWorkDate(dateInput);
  const showDateError = dateInputInvalid && !dateFocused;
  const cutoffLabel = data?.onTimeDeadlineLabel || FALLBACK_LABELS.cutoff;
  const opsOffLabel = data?.opsOffEmailLabel || FALLBACK_LABELS.opsOff;
  const recheckLabel = data?.lateRecheckLabel || FALLBACK_LABELS.recheck;
  const lateOpsLabel = data?.lateOpsEmailLabel || FALLBACK_LABELS.lateOps;

  const expected = report?.summary.expectedInOffice || 0;
  const onTime = report?.summary.onTimeCount || 0;
  const emailUnavailable = report?.summary.emailUnavailableCount || 0;
  const complianceEligible = Math.max(0, expected - emailUnavailable);
  const compliancePct = complianceEligible > 0
    ? Math.min(100, Math.round((onTime / complianceEligible) * 100))
    : null;
  const lateCount = report?.summary.lateCount || 0;
  const noCheckinCount = (report?.summary.noClickCount || 0) + (report?.summary.offsiteCount || 0);
  const pendingCount = report?.summary.pendingCount || 0;
  // Holds each KPI caption's line while the report loads, so tiles don't change height.
  const pendingCaption = '\u00a0';

  const goToDate = (workDate: string) => {
    setSelectedWorkDate(workDate);
    setDateInput(workDate);
  };

  const onDateInputChange = (value: string) => {
    setDateInput(value);
    if (isWorkDate(value)) setSelectedWorkDate(value);
  };

  const refreshAll = () => {
    fetchReport(selectedWorkDate);
    fetchAnalytics(selectedWorkDate, analyticsDays);
    fetchEmployeeHistory(selectedEmployeeId, selectedWorkDate, analyticsDays);
  };

  const refreshing = loading && Boolean(data);
  const headerActions = (
    <Button
      variant="outlined"
      startIcon={refreshing ? <CircularProgress size={16} thickness={5} aria-hidden="true" /> : <RotateCw size={16} />}
      onClick={loading ? undefined : refreshAll}
      aria-busy={loading || undefined}
    >
      {refreshing ? 'Refreshing…' : 'Refresh report'}
    </Button>
  );

  const dateToolbar = (
    <Box sx={{ display: 'grid', gap: 0.75 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5 }}>
          <Tooltip title="Previous day">
            <IconButton aria-label="Previous day" onClick={() => goToDate(addDaysToYmd(selectedWorkDate, -1))}>
              <ChevronLeft />
            </IconButton>
          </Tooltip>
          <TextField
            type="date"
            label="Work date"
            value={dateInput}
            error={showDateError}
            onChange={(event) => onDateInputChange(event.target.value)}
            onFocus={() => setDateFocused(true)}
            onBlur={() => setDateFocused(false)}
            slotProps={{ htmlInput: { 'aria-describedby': showDateError ? 'checkin-work-date-error' : undefined } }}
            sx={{ width: 172, '& input': { fontVariantNumeric: 'tabular-nums' } }}
          />
          <Tooltip title="Next day">
            <IconButton aria-label="Next day" onClick={() => goToDate(addDaysToYmd(selectedWorkDate, 1))}>
              <ChevronRight />
            </IconButton>
          </Tooltip>
        </Box>
        <Tooltip title={isToday ? 'Already showing today' : ''}>
          <span>
            <Button variant="text" disabled={isToday} onClick={() => goToDate(getTodayEt())}>
              Go to today
            </Button>
          </span>
        </Tooltip>
      </Box>
      {showDateError && (
        <Typography id="checkin-work-date-error" variant="body2" component="p" sx={{ color: sbg.status.danger.text }}>
          Enter a full date, e.g. 10/08/2026. Still showing {formatDate(selectedWorkDate)}.
        </Typography>
      )}
    </Box>
  );

  const trendsDescription = analytics && !analyticsBusy
    ? `${formatDateRange(analytics.startDate, analytics.endDate)} · ${formatCount(analytics.daysRequested, 'calendar day')}`
    : `Last ${formatCount(analyticsDays, 'calendar day')} through ${formatDate(selectedWorkDate)}`;

  return (
    <Box sx={{ display: 'grid', gap: 3, minWidth: 0 }}>
      <PageHeader
        overline="Attendance"
        title="Office check-in"
        meta={[
          'Sales floor',
          dateLabel,
          `On time by ${cutoffLabel}`,
          reportCurrent && !error && reportUpdatedAt ? `Updated ${formatTime(reportUpdatedAt)}` : null,
        ]}
        actions={headerActions}
        sx={{ mb: 0 }}
      >
        {dateToolbar}
      </PageHeader>

      {/* cronEnabled is a server setting, not per date, so the alert stays put while a report reloads. */}
      {data && !data.cronEnabled && !error && (
        <Alert severity="warning">
          The check-in schedule is paused, so the {CHECKIN_EMAIL_WINDOW.replace(' ET', '')} emails, {opsOffLabel.replace(' ET', '')} turn-off
          list, {recheckLabel.replace(' ET', '')} recheck and {lateOpsLabel} late list won’t run. Ask engineering to turn the check-in
          schedule back on.
        </Alert>
      )}

      {error ? (
        <SectionCard>
          <ErrorState
            title={`Couldn’t load check-ins for ${dateLabel}`}
            body="The server didn’t return the report. Try again in a minute, or pick another date."
            details={error}
            onRetry={() => fetchReport(selectedWorkDate)}
          />
        </SectionCard>
      ) : (
        <>
          <KpiGrid sx={{ mb: 0 }}>
            <KpiTile
              featured
              label="On-time check-in"
              value={compliancePct === null ? MISSING : formatPercent(compliancePct)}
              caption={
                report
                  ? [
                      `${formatNumber(onTime)} of ${formatCount(complianceEligible, 'eligible rep')}`,
                      emailUnavailable > 0 ? `${formatNumber(emailUnavailable)} excused` : null,
                      shortDateLabel,
                    ]
                      .filter(Boolean)
                      .join(' · ')
                  : pendingCaption
              }
              info={
                emailUnavailable > 0
                  ? `Eligible reps who checked in from the office by ${cutoffLabel}. ${formatCount(emailUnavailable, 'rep')} whose check-in email wasn’t delivered ${emailUnavailable === 1 ? 'is' : 'are'} excused and left out.`
                  : `Eligible reps who checked in from the office by ${cutoffLabel}. Reps whose check-in email isn’t delivered are excused and left out.`
              }
              loading={reportBusy}
            />
            <KpiTile
              label="Late arrivals"
              value={formatCount(lateCount, 'rep')}
              caption={report ? `After ${cutoffLabel} · rechecked ${recheckLabel}` : pendingCaption}
              loading={reportBusy}
            />
            <KpiTile
              label="No office check-in"
              value={formatCount(noCheckinCount, 'rep')}
              caption={
                !report
                  ? pendingCaption
                  : pendingCount > 0
                    ? `${formatCount(pendingCount, 'rep')} still awaiting check-in`
                    : `${formatNumber(report.summary.noClickCount)} no click · ${formatNumber(report.summary.offsiteCount)} offsite`
              }
              loading={reportBusy}
            />
            <KpiTile
              label="Not expected"
              value={formatCount(report?.summary.excludedCount || 0, 'rep')}
              caption={report ? `Of ${formatCount(report.summary.totalSalesReps, 'rep')} on the sales roster` : pendingCaption}
              loading={reportBusy}
            />
          </KpiGrid>

          <Box
            sx={{
              display: 'grid',
              gap: 3,
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 7fr) minmax(0, 5fr)' },
              alignItems: 'stretch',
            }}
          >
            <TurnOffList
              rows={opsOffRows}
              busy={reportBusy}
              showSkeleton={showReportSkeleton}
              expected={expected}
              excused={emailUnavailable}
              pending={pendingCount}
              opsOffLabel={opsOffLabel}
              cutoffLabel={cutoffLabel}
              onSelect={setSelectedEmployeeId}
            />
            <ScheduleCard data={report} busy={reportBusy} showSkeleton={showReportSkeleton} workDate={selectedWorkDate} />
          </Box>

          <Box component="section" aria-labelledby="checkin-roster-heading" sx={{ display: 'grid', gap: 2, minWidth: 0 }}>
            <SectionHeading
              id="checkin-roster-heading"
              title="Check-in roster"
              description={
                report
                  ? `${formatCount(report.summary.totalSalesReps, 'sales rep')} · ${formatNumber(expected)} expected in the office on ${shortDateLabel}`
                  : 'Every sales rep on the roster, with their check-in email and office click.'
              }
            />
            <RosterTable
              roster={report?.roster || []}
              busy={reportBusy}
              dateLabel={dateLabel}
              onSelect={setSelectedEmployeeId}
            />
          </Box>
        </>
      )}

      <Box component="section" aria-labelledby="checkin-trends-heading" sx={{ display: 'grid', gap: 3, mt: 2, minWidth: 0 }}>
        <SectionHeading
          id="checkin-trends-heading"
          title="Check-in trends"
          description={trendsDescription}
          actions={
            <>
              {/* Always mounted so the controls beside it never shift; visible only while trends refresh in place. */}
              <CircularProgress
                size={16}
                thickness={5}
                aria-label={analyticsRefreshing ? 'Updating trends' : undefined}
                aria-hidden={analyticsRefreshing ? undefined : true}
                sx={{ mb: 1.25, flexShrink: 0, visibility: analyticsRefreshing ? 'visible' : 'hidden' }}
              />
              <SegmentedControl<AnalyticsWindow>
                aria-label="Trend window"
                value={String(analyticsDays) as AnalyticsWindow}
                onChange={(value) => setAnalyticsDays(Number(value))}
                options={ANALYTICS_WINDOWS.map((days) => ({ value: days, label: `${days} days` }))}
              />
              <Autocomplete
                size="small"
                options={employeeOptions}
                value={employeeOptions.find((person) => person.employeeId === selectedEmployeeId) ?? null}
                onChange={(_, person) => setSelectedEmployeeId(person?.employeeId ?? '')}
                getOptionLabel={(person) => person.name}
                isOptionEqualToValue={(option, value) => option.employeeId === value.employeeId}
                noOptionsText="No reps in this window"
                renderOption={(props, person) => {
                  const { key, ...optionProps } = props as React.HTMLAttributes<HTMLLIElement> & { key: React.Key };
                  return (
                    <li key={key} {...optionProps}>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0, width: '100%' }}>
                        <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {person.name}
                        </Box>
                        <Box component="span" sx={{ ml: 'auto', flexShrink: 0, color: sbg.text.secondary }}>
                          {person.teamName || 'Sales'}
                        </Box>
                      </Box>
                    </li>
                  );
                }}
                sx={{ width: { xs: '100%', sm: 280 } }}
                renderInput={(params) => <TextField {...params} label="Rep history" placeholder="Search by name" />}
              />
            </>
          }
        />

        {analyticsError ? (
          <SectionCard>
            <ErrorState
              compact
              title="Couldn’t load check-in trends"
              body="The server didn’t return trend data for this window. Try again in a minute."
              details={analyticsError}
              onRetry={() => fetchAnalytics(selectedWorkDate, analyticsDays)}
            />
          </SectionCard>
        ) : analyticsBusy ? (
          <TrendsSkeleton />
        ) : analytics ? (
          <>
            <SpecGrid
              minColumnWidth={200}
              items={[
                {
                  label: 'On-time rate',
                  value: formatPercent(analytics.overallOnTimeRate),
                  caption: `${formatNumber(analytics.expectedRecords)} expected rep-days`,
                },
                {
                  label: 'Issue rate',
                  value: formatPercent(analytics.issueRate),
                  caption: 'Late, no click or offsite',
                },
                {
                  label: 'Late rate',
                  value: formatPercent(analytics.lateRate),
                  caption: `Checked in after ${cutoffLabel}`,
                },
                {
                  label: 'Records',
                  value: formatNumber(analytics.recordsAnalyzed),
                  caption: `Check-in days from ${formatDateRange(analytics.startDate, analytics.endDate)}`,
                },
              ]}
            />

            <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' } }}>
              <Leaderboard
                title="Most often late"
                subtitle="Most late check-ins in this window."
                rows={analytics.chronicLate}
                emptyText="No late check-in pattern in this window."
                metricLabel="late"
                metricFor={(row) => formatNumber(row.lateCount)}
                detailFor={(row) => `${formatPercent(row.lateRate)} late · ${formatCount(row.expectedDays, 'expected day')}`}
                onSelect={setSelectedEmployeeId}
              />
              <Leaderboard
                title="Earliest arrivals"
                subtitle="Earliest average first check-in."
                rows={analytics.earliestCheckins}
                emptyText="Not enough check-ins for an early-arrival ranking yet."
                metricLabel="average"
                metricFor={(row) => row.averageCheckinLabel || MISSING}
                detailFor={(row) => formatCount(row.checkinCount, 'check-in')}
                onSelect={setSelectedEmployeeId}
              />
              <Leaderboard
                title="Most consistent"
                subtitle="Best on-time rate, less penalties for misses."
                rows={analytics.mostConsistent}
                emptyText="Not enough expected days for consistency scoring yet."
                metricLabel="score"
                metricFor={(row) => formatNumber(row.consistencyScore)}
                detailFor={(row) => `${formatPercent(row.onTimeRate)} on time · ${formatCount(row.expectedDays, 'expected day')}`}
                onSelect={setSelectedEmployeeId}
              />
              <Leaderboard
                title="Needs follow-up"
                subtitle="Most late, no-click or offsite days."
                rows={analytics.mostMissed}
                emptyText="No follow-up issues in this window."
                metricLabel="issues"
                metricFor={(row) => formatNumber(row.issueCount)}
                detailFor={(row) => `${formatNumber(row.noClickCount)} no click · ${formatNumber(row.offsiteCount)} offsite`}
                onSelect={setSelectedEmployeeId}
              />
            </Box>

            <TeamReliability teams={analytics.teamReliability} />
          </>
        ) : (
          <SectionCard>
            <EmptyState
              title="No trend data yet"
              body="Trends appear after the first weekday check-in in this window."
            />
          </SectionCard>
        )}
      </Box>

      <Sheet
        open={Boolean(selectedEmployeeId)}
        onClose={() => setSelectedEmployeeId('')}
        size="lg"
        title={selectedPerson?.name || 'Check-in history'}
        subtitle={`${selectedPerson ? `${selectedPerson.teamName || 'Sales'} · ` : ''}Last ${formatCount(analyticsDays, 'day')} through ${formatDate(selectedWorkDate)}`}
      >
        <EmployeeHistoryBody
          history={employeeHistory}
          loading={employeeHistoryLoading}
          error={employeeHistoryError}
          onRetry={() => fetchEmployeeHistory(selectedEmployeeId, selectedWorkDate, analyticsDays)}
        />
      </Sheet>
    </Box>
  );
};

export default SalesOfficeCheckinManager;
